import { useState, useCallback } from 'react';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ProjectTypeGlyph } from '@/components/ui/ProjectTypeGlyph/ProjectTypeGlyph';
import {
  PROJECT_TYPES,
  PROJECT_TYPE_DISPLAY,
  getProjectTypeColor,
  type ProjectTypeValue,
} from '@/utils/projectTypeColors';
import { ProjectTypeSymbol } from '@/utils/ProjectTypeSymbol';
import './ProjectTypeCell.css';

interface ProjectTypeCellProps {
  value: string | null | undefined;
  projectId: string;
  onChange: (projectId: string, newType: ProjectTypeValue | null) => void;
  disabled?: boolean;
}

export function ProjectTypeCell({ value, projectId, onChange, disabled }: ProjectTypeCellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const typeKey = (value && PROJECT_TYPES.includes(value as ProjectTypeValue)) ? value as ProjectTypeValue : 'Common';
  const config = PROJECT_TYPE_DISPLAY[typeKey];

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      e.stopPropagation();
      setMenuAnchorEl(e.currentTarget);
      setMenuOpen((prev) => !prev);
    },
    [disabled]
  );

  const handleSelect = useCallback(
    (selectedType: ProjectTypeValue | null) => {
      onChange(projectId, selectedType);
      setMenuOpen(false);
      setMenuAnchorEl(null);
    },
    [projectId, onChange]
  );

  return (
    <>
      <div className="projects-table__type-cell-inner">
        <button
          type="button"
          className="projects-table__type-btn"
          onClick={handleOpen}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          aria-label={`Тип: ${config.text}`}
        >
          <ProjectTypeGlyph type={typeKey} fallbackToCommon />
        </button>
      </div>
      <ContextMenu
        isOpen={menuOpen}
        onClose={() => {
          setMenuOpen(false);
          setMenuAnchorEl(null);
        }}
        anchorPosition={null}
        anchorEl={menuAnchorEl}
        alignY="center"
      >
        <ul className="context-menu__list context-menu__list_type" role="listbox">
          {PROJECT_TYPES.map((key) => {
            const cfg = PROJECT_TYPE_DISPLAY[key];
            const itemColor = getProjectTypeColor(key);
            const isDot = key === 'Common';
            return (
              <li key={key} role="option">
                <button
                  type="button"
                  className={`context-menu__action context-menu__action_type ${value === key ? 'context-menu__action_selected' : ''}`}
                  onClick={() => handleSelect(key)}
                  role="menuitem"
                  style={{ ['--item-color' as string]: itemColor }}
                >
                  <span className={`project-type-cell__menu-label ${isDot ? 'project-type-cell__menu-label_dot' : ''}`} style={{ color: itemColor }}>
                    <ProjectTypeSymbol symbol={cfg.label} />
                  </span>
                  <span className="context-menu__action-text">{cfg.text}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </ContextMenu>
    </>
  );
}
